// ─────────────────────────────────────────────────────────────────────────────
// Preferencias de apariencia (LOCAL, sin backend).
//
// Tres ejes ORTOGONALES:
//
//   1) Theme (3 mutuamente excluyentes):
//      • original       → sin overrides (naranjo Beartrack + look por defecto).
//      • beartrack      → sidebar como el login (gradiente naranjo, texto blanco);
//                         clase `.theme-beartrack` en <html>, CSS lo materializa.
//      • personalizado  → cambia el acento de la marca a uno de los presets
//                         curados (BRAND_PRESETS). Aplica sobre --brand-* y
//                         --color-primary*. NO incluye color picker libre.
//
//   2) Daltónico (boolean, INDEPENDIENTE del theme):
//      Re-mapea solo --status-* a la paleta Okabe–Ito. Sirve para que los tags
//      de estado de paquete sean más distinguibles. Como solo toca status,
//      cualquier theme puede tenerlo prendido o apagado.
//
//   3) Densidad ('auto' | 'comfortable' | 'compact', INDEPENDIENTE de los otros
//      dos): materializa `data-density` en <html>. El CSS que la consume es
//      _density-compact.scss — 'compact' fuerza la capa compacta en cualquier
//      alto de pantalla, 'comfortable' desactiva también la puerta automática
//      de viewport bajo, 'auto' deja el comportamiento histórico (compactar
//      solo a ≤800px de alto). Solo geometría: no toca color, así que theme y
//      daltónico quedan intactos por construcción.
//
// Se aplica sobre `document.documentElement` (clase + atributo + style inline)
// y se re-aplica al cambiar light/dark desde ThemeProvider. Sincroniza entre
// componentes vía evento de window.
// ─────────────────────────────────────────────────────────────────────────────

export const THEME_MODE_KEY = "beartrack.theme.mode"; // 'original' | 'beartrack' | 'personalizado'
export const THEME_BRAND_KEY = "beartrack.theme.brand"; // hex del preset elegido cuando mode === 'personalizado'
export const THEME_COLORBLIND_KEY = "beartrack.theme.colorblind"; // '1' | '0'
export const THEME_DENSITY_KEY = "beartrack.theme.density"; // 'auto' | 'comfortable' | 'compact'
export const THEME_EVENT = "beartrack:theme-changed";

// Presets curados para el modo "Personalizado". Pensados para mantener
// coherencia con el sistema de Beartrack (logos, sidebar, badges) — sin
// permitir cualquier hex random.
export const BRAND_PRESETS = [
  { key: "indigo",   name: "Índigo profesional", hex: "#6366F1" },
  { key: "emerald",  name: "Esmeralda operacional", hex: "#10B981" },
  { key: "violet",   name: "Violeta distinguido", hex: "#8B5CF6" },
  { key: "rose",     name: "Rosa cálido", hex: "#F43F5E" },
];

// Paleta de estados apta para daltonismo (Okabe–Ito). Tokens = los que consumen
// las pills de estado (.status-badge--*): delivered→success, route→warning,
// failed→error, picked→info, storage→reverse. neutral se deja gris (no aplica).
const COLORBLIND = {
  success: "#009E73",
  warning: "#E69F00",
  error: "#D55E00",
  info: "#56B4E9",
  reverse: "#CC79A7",
};

// Paleta ORIGINAL de estados (espejo de los tokens base de design-tokens.scss,
// light/dark). Se mantiene aquí para poder PREVISUALIZAR los colores por defecto
// aunque el daltónico esté activo globalmente (que pisa --status-* en :root).
// Cada token = [fg "r g b", soft "r g b"].
const ORIGINAL_STATUS = {
  light: {
    success: ["34 158 92", "220 252 231"],
    warning: ["234 88 12", "255 237 213"],
    error: ["220 38 38", "254 226 226"],
    info: ["0 132 255", "219 234 254"],
    reverse: ["147 51 234", "243 232 255"],
    neutral: ["102 102 102", "244 244 245"],
  },
  dark: {
    success: ["51 184 113", "6 78 59"],
    warning: ["251 191 36", "120 53 15"],
    error: ["248 113 113", "127 29 29"],
    info: ["96 165 250", "30 58 138"],
    reverse: ["192 132 252", "88 28 135"],
    neutral: ["163 163 163", "30 30 30"],
  },
};

// ── helpers de color ──────────────────────────────────────────────────────────
function hexToRgb(hex) {
  let h = String(hex || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return { r: 249, g: 115, b: 22 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return { h, s, l };
}
function hslToRgb(h, s, l) {
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}
const triplet = ({ r, g, b }) => `${r} ${g} ${b}`;
const commaRgb = ({ r, g, b }) => `${r}, ${g}, ${b}`;
const toHex = ({ r, g, b }) =>
  "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
const rgba = ({ r, g, b }, a) => `rgba(${r}, ${g}, ${b}, ${a})`;

// ── generación de variables ─────────────────────────────────────────────────
function brandVars(baseHex, isDark) {
  const base = hexToRgb(baseHex);
  const { h, s } = rgbToHsl(base);
  const at = (l, sat = s) => triplet(hslToRgb(h, sat, l));
  const vars = {};

  if (!isDark) {
    vars["--brand-50"] = at(0.97);
    vars["--brand-100"] = at(0.94);
    vars["--brand-200"] = at(0.86);
    vars["--brand-300"] = at(0.77);
    vars["--brand-400"] = at(0.66);
    vars["--brand-500"] = triplet(base);
    vars["--brand-600"] = at(0.46);
    vars["--brand-700"] = at(0.39);
    vars["--brand-800"] = at(0.32);
    vars["--brand-900"] = at(0.26);
    vars["--color-primary"] = baseHex;
    vars["--color-primary-light"] = toHex(hslToRgb(h, s, 0.62));
    vars["--color-primary-dark"] = toHex(hslToRgb(h, s, 0.46));
    vars["--color-primary-alpha"] = rgba(base, 0.12);
    vars["--color-primary-rgb"] = commaRgb(base);
    // Sidebar tintado en modo claro: difuminado con blanco. Saturación recortada
    // a 0.45 para que sea un velo y no un color saturado; lightness alta para
    // que el texto oscuro por defecto siga legible.
    const sidebarSat = Math.min(s, 0.45);
    vars["--sidebar-bg-top"] = toHex(hslToRgb(h, sidebarSat, 0.97));
    vars["--sidebar-bg-bot"] = toHex(hslToRgb(h, sidebarSat, 0.92));
    vars["--sidebar-accent-on-tint"] = toHex(hslToRgb(h, s, 0.42));
  } else {
    vars["--brand-50"] = at(0.12, Math.min(s, 0.7));
    vars["--brand-100"] = at(0.18, Math.min(s, 0.75));
    vars["--brand-200"] = at(0.3);
    vars["--brand-300"] = at(0.42);
    vars["--brand-400"] = at(0.66);
    vars["--brand-500"] = triplet(base);
    vars["--brand-600"] = at(0.58);
    vars["--brand-700"] = at(0.72);
    vars["--brand-800"] = at(0.64);
    vars["--brand-900"] = at(0.8);
    const bright = hslToRgb(h, s, 0.66);
    vars["--color-primary"] = toHex(bright);
    vars["--color-primary-light"] = toHex(hslToRgb(h, s, 0.74));
    vars["--color-primary-dark"] = baseHex;
    vars["--color-primary-alpha"] = rgba(bright, 0.16);
    vars["--color-primary-rgb"] = commaRgb(bright);
    // Sidebar tintado en modo oscuro: difuminado con negro. Lightness baja
    // pero con la suficiente saturación para que se reconozca el acento.
    const sidebarSat = Math.min(s, 0.55);
    vars["--sidebar-bg-top"] = toHex(hslToRgb(h, sidebarSat, 0.16));
    vars["--sidebar-bg-bot"] = toHex(hslToRgb(h, sidebarSat, 0.08));
    vars["--sidebar-accent-on-tint"] = toHex(hslToRgb(h, s, 0.72));
  }
  return vars;
}

// Tinte sutil de superficies: aplica el HUE del tema con saturación muy baja
// a TODOS los backgrounds del chrome (--bg, --bg-muted, --surface-*, etc.)
// para que el tema se sienta global, no solo en el sidebar.
//
// La idea: una superficie "blanca" en tema Indigo no es #FFFFFF puro sino
// HSL(indigoH, ~3%, 99.5%) — un blanco con una gota de azul. Lo mismo en
// dark: en vez de #0A0A0A puro, HSL(indigoH, ~6%, 5%) — negro con una pizca.
//
// Saturación deliberadamente recortada (≤ 0.06 en dark, ≤ 0.04 en claro) para
// que sea velo, no color. El contraste de texto se preserva porque el delta L
// vs los fg-tokens es prácticamente el mismo.
function surfaceVars(baseHex, isDark) {
  const base = hexToRgb(baseHex);
  const { h } = rgbToHsl(base);
  const vars = {};

  if (!isDark) {
    // Subimos un punto la sat respecto al primer ajuste — antes era invisible
    // en colores cálidos sobre fondo blanco. Sigue siendo "blanco con gotas":
    // un blanco indigo a sat=0.06 ya se distingue del blanco puro al lado.
    const sat = 0.06;
    const satMid = 0.085; // un poco más para superficies hundidas
    // RGB triplets (design-tokens.scss → tailwind `rgb(var(--bg))`)
    vars["--bg"]        = triplet(hslToRgb(h, sat,    0.995));
    vars["--bg-muted"]  = triplet(hslToRgb(h, sat,    0.978));
    vars["--bg-subtle"] = triplet(hslToRgb(h, satMid, 0.958));
    // Hex tokens (_theme-variables.scss → usados directos)
    vars["--bg-primary"]     = toHex(hslToRgb(h, sat,    0.995));
    vars["--bg-secondary"]   = toHex(hslToRgb(h, satMid, 0.962));
    vars["--bg-tertiary"]    = toHex(hslToRgb(h, satMid, 0.948));
    vars["--bg-elevated"]    = toHex(hslToRgb(h, sat,    0.995));
    vars["--surface-primary"]   = toHex(hslToRgb(h, sat,    0.995));
    vars["--surface-secondary"] = toHex(hslToRgb(h, satMid, 0.948));
    vars["--surface-tertiary"]  = toHex(hslToRgb(h, satMid, 0.918));
    vars["--surface-hover"]     = toHex(hslToRgb(h, sat,    0.962));
    vars["--surface-active"]    = toHex(hslToRgb(h, satMid, 0.882));
    // Bordes también con tinte (apenas, para que no rompan la cohesión)
    vars["--border-primary"]   = toHex(hslToRgb(h, satMid, 0.838));
    vars["--border-secondary"] = toHex(hslToRgb(h, satMid, 0.892));
    vars["--border-light"]     = toHex(hslToRgb(h, satMid, 0.948));
  } else {
    // En dark la sat se puede empujar más sin perder legibilidad — los grises
    // oscuros leen el tinte mejor que los blancos. 0.10/0.13 ya se siente
    // "negro azulado" / "negro terracota" sin volverse color saturado.
    const sat = 0.10;
    const satMid = 0.13;
    vars["--bg"]        = triplet(hslToRgb(h, sat,    0.04));
    vars["--bg-muted"]  = triplet(hslToRgb(h, sat,    0.078));
    vars["--bg-subtle"] = triplet(hslToRgb(h, satMid, 0.11));
    vars["--bg-primary"]     = toHex(hslToRgb(h, sat,    0.04));
    vars["--bg-secondary"]   = toHex(hslToRgb(h, satMid, 0.078));
    vars["--bg-tertiary"]    = toHex(hslToRgb(h, satMid, 0.12));
    vars["--bg-elevated"]    = toHex(hslToRgb(h, satMid, 0.14));
    vars["--surface-primary"]   = toHex(hslToRgb(h, sat,    0.078));
    vars["--surface-secondary"] = toHex(hslToRgb(h, satMid, 0.12));
    vars["--surface-tertiary"]  = toHex(hslToRgb(h, satMid, 0.16));
    vars["--surface-hover"]     = toHex(hslToRgb(h, sat,    0.14));
    vars["--surface-active"]    = toHex(hslToRgb(h, satMid, 0.18));
    vars["--border-primary"]   = toHex(hslToRgb(h, satMid, 0.2));
    vars["--border-secondary"] = toHex(hslToRgb(h, satMid, 0.16));
    vars["--border-light"]     = toHex(hslToRgb(h, satMid, 0.12));
  }
  return vars;
}

function statusVars(isDark) {
  const vars = {};
  Object.entries(COLORBLIND).forEach(([token, hex]) => {
    const base = hexToRgb(hex);
    const { h, s } = rgbToHsl(base);
    if (!isDark) {
      vars[`--status-${token}`] = triplet(base);
      vars[`--status-${token}-soft`] = triplet(hslToRgb(h, Math.min(s, 0.85), 0.92));
    } else {
      vars[`--status-${token}`] = triplet(hslToRgb(h, s, 0.7));
      vars[`--status-${token}-soft`] = triplet(hslToRgb(h, Math.min(s, 0.6), 0.16));
    }
  });
  return vars;
}

// Devuelve un objeto de estilo (variables CSS inline) que materializa una
// paleta de estados COMPLETA y AISLADA del estado global. Pensado para
// scopearlo en un contenedor de previsualización: como las CSS vars cascadean,
// los `.status-badge--*` de adentro leen estos valores y no los de :root.
//   kind: 'original' | 'colorblind'
export function statusPreviewStyle(kind, isDark) {
  const out = {};
  const orig = ORIGINAL_STATUS[isDark ? "dark" : "light"];
  // Base = original (cubre neutral, que el daltónico no remapea).
  Object.entries(orig).forEach(([token, [fg, soft]]) => {
    out[`--status-${token}`] = fg;
    out[`--status-${token}-soft`] = soft;
  });
  // En daltónico, pisar solo los tokens de la paleta Okabe–Ito.
  if (kind === "colorblind") {
    Object.entries(statusVars(isDark)).forEach(([k, v]) => {
      out[k] = v;
    });
  }
  return out;
}

const BRAND_KEYS = [
  "--brand-50", "--brand-100", "--brand-200", "--brand-300", "--brand-400",
  "--brand-500", "--brand-600", "--brand-700", "--brand-800", "--brand-900",
  "--color-primary", "--color-primary-light", "--color-primary-dark",
  "--color-primary-alpha", "--color-primary-rgb",
  "--sidebar-bg-top", "--sidebar-bg-bot", "--sidebar-accent-on-tint",
];

// Tokens de superficie que el tinte global pisa. Se listan aparte para que
// `applyBrandTheme` los limpie cuando el modo es `original` y vuelvan a regir
// los valores base de _theme-variables.scss / design-tokens.scss.
const SURFACE_KEYS = [
  "--bg", "--bg-muted", "--bg-subtle",
  "--bg-primary", "--bg-secondary", "--bg-tertiary", "--bg-elevated",
  "--surface-primary", "--surface-secondary", "--surface-tertiary",
  "--surface-hover", "--surface-active",
  "--border-primary", "--border-secondary", "--border-light",
];

// Color base para tintar en modo 'beartrack' (no es 'personalizado' — no
// elige el brand del picker — pero igual queremos que el tema se sienta
// global. Usamos el naranja Beartrack hardcodeado).
const BEARTRACK_HEX = "#F97316";
const STATUS_KEYS = Object.keys(COLORBLIND).flatMap((t) => [
  `--status-${t}`, `--status-${t}-soft`,
]);

const VALID_MODES = new Set(["original", "beartrack", "personalizado"]);
const VALID_DENSITIES = new Set(["auto", "comfortable", "compact"]);

// ── API pública ───────────────────────────────────────────────────────────────
export function getMode() {
  try {
    const m = localStorage.getItem(THEME_MODE_KEY);
    return VALID_MODES.has(m) ? m : "original";
  } catch {
    return "original";
  }
}

export function getBrand() {
  try {
    return localStorage.getItem(THEME_BRAND_KEY) || BRAND_PRESETS[0].hex;
  } catch {
    return BRAND_PRESETS[0].hex;
  }
}

export function getColorblind() {
  try {
    return localStorage.getItem(THEME_COLORBLIND_KEY) === "1";
  } catch {
    return false;
  }
}

export function getDensity() {
  // Default = "compact" (decisión 2026-07-30): la escala 90% es el estándar;
  // Automática y Cómoda quedan como opt-out en Mi cuenta. Espejado en el
  // script pre-paint de public/index.html — cambiar AMBOS o hay FOUC.
  try {
    const d = localStorage.getItem(THEME_DENSITY_KEY);
    return VALID_DENSITIES.has(d) ? d : "compact";
  } catch {
    return "compact";
  }
}

export function applyBrandTheme() {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const isDark =
    root.classList.contains("dark") || root.getAttribute("data-theme") === "dark";
  const mode = getMode();
  const colorblind = getColorblind();

  // Limpiar overrides previos → vuelven a regir los valores del stylesheet.
  [...BRAND_KEYS, ...SURFACE_KEYS, ...STATUS_KEYS].forEach((k) =>
    root.style.removeProperty(k),
  );

  // Eje 1: theme (clase + posibles overrides de marca + tinte global).
  root.classList.toggle("theme-beartrack", mode === "beartrack");
  root.classList.toggle("theme-personalizado", mode === "personalizado");

  // Color base para el tinte global. En personalizado = preset del usuario.
  // En beartrack = naranja Beartrack. En original = no tintamos (el chrome
  // queda con los grises/blancos/negros neutros del stylesheet base).
  let tintHex = null;
  if (mode === "personalizado") {
    tintHex = getBrand();
    Object.entries(brandVars(tintHex, isDark)).forEach(([k, v]) =>
      root.style.setProperty(k, v),
    );
  } else if (mode === "beartrack") {
    tintHex = BEARTRACK_HEX;
  }
  if (tintHex) {
    Object.entries(surfaceVars(tintHex, isDark)).forEach(([k, v]) =>
      root.style.setProperty(k, v),
    );
  }

  // Eje 2: daltónico (status overrides, INDEPENDIENTE del theme).
  if (colorblind) {
    Object.entries(statusVars(isDark)).forEach(([k, v]) => root.style.setProperty(k, v));
  }

  // Eje 3: densidad. Solo materializa el atributo; el CSS que lo consume vive
  // en _density-compact.scss. Se setea SIEMPRE (también 'auto') para que el
  // estado sea inspeccionable en el DOM y no dependa de ausencia de atributo.
  root.setAttribute("data-density", getDensity());
}

export function setThemePref({ mode, brand, colorblind, density } = {}) {
  try {
    if (mode !== undefined && VALID_MODES.has(mode)) {
      localStorage.setItem(THEME_MODE_KEY, mode);
    }
    if (brand !== undefined) localStorage.setItem(THEME_BRAND_KEY, brand);
    if (colorblind !== undefined) {
      localStorage.setItem(THEME_COLORBLIND_KEY, colorblind ? "1" : "0");
    }
    if (density !== undefined && VALID_DENSITIES.has(density)) {
      localStorage.setItem(THEME_DENSITY_KEY, density);
    }
  } catch {
    // localStorage no disponible
  }
  applyBrandTheme();
  try {
    window.dispatchEvent(new CustomEvent(THEME_EVENT));
  } catch {
    // sin window
  }
}

let inited = false;
export function initBrandTheme() {
  if (inited || typeof window === "undefined") return;
  inited = true;
  applyBrandTheme();
  window.addEventListener(THEME_EVENT, applyBrandTheme);
  window.addEventListener("storage", (e) => {
    if (
      e.key === THEME_MODE_KEY ||
      e.key === THEME_BRAND_KEY ||
      e.key === THEME_COLORBLIND_KEY ||
      e.key === THEME_DENSITY_KEY
    ) {
      applyBrandTheme();
    }
  });
}
